import { formatDateTime } from "@/lib/format";
import { useEffect, useState, useCallback } from "react";
import { glopicApi } from "@/lib/glopic-api";
import Pagination from "@/components/base/Pagination";
import FilterBar from "@/components/base/FilterBar";
import TableSortHeader from "@/components/base/TableSortHeader";
import UserIdentityCell from "@/components/base/UserIdentityCell";

interface AgentTraceRow {
  id: number;
  task_id: string | null;
  gen_id: string | null;
  user_id: string | null;
  nickname: string | null;
  avatar: string | null;
  agent_type: string;
  input: string | null;
  output: string | null;
  status: string;
  latency_ms: number | null;
  error_msg: string | null;
  model_used: string | null;
  created_at: string;
}

const agentTypeLabels: Record<string, string> = {
  normalize: "提示词规范化",
  enhance: "提示词增强",
  safety_check: "安全检测",
  translate: "翻译",
  summarize: "摘要",
};

const statusLabels: Record<string, { cls: string; label: string }> = {
  success: { cls: "bg-emerald-500/10 text-emerald-500", label: "成功" },
  failed: { cls: "bg-red-500/10 text-red-500", label: "失败" },
  timeout: { cls: "bg-amber-500/10 text-amber-500", label: "超时" },
};

export default function AiAuditAgentTracePage() {
  const [traces, setTraces] = useState<AgentTraceRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [agentTypeFilter, setAgentTypeFilter] = useState<string>("");
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [genIdFilter, setGenIdFilter] = useState("");
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [sortField, setSortField] = useState("created_at");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");

  const handleSort = useCallback((field: string, dir: "asc" | "desc") => {
    setSortField(field || "created_at");
    setSortDir(dir);
    setPage(1);
  }, []);

  const fetchTraces = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, string | number> = { page, page_size: pageSize };
      if (statusFilter) params.status = statusFilter;

      const response = await glopicApi.get("/audit/agent-trace", { params });
      const data = response.data.data;
      setTraces(data.items ?? []);
      setTotal(data.total ?? 0);
    } catch (err) {
      console.error("Fetch agent traces error:", err);
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agentTypeFilter, statusFilter, genIdFilter, sortField, sortDir, page, pageSize]);

  useEffect(() => {
    fetchTraces();
  }, [fetchTraces]);

  const totalPages = Math.ceil(total / pageSize);

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-text-primary">Agent 追踪日志</h2>
          <p className="text-text-secondary text-sm mt-1">
            共 {total} 条执行记录
          </p>
        </div>
      </div>

      {/* Filters */}
      <FilterBar
        search={genIdFilter}
        onSearchChange={(val) => { setGenIdFilter(val); setPage(1); }}
        searchPlaceholder="搜索生成ID..."
        filters={[
          {
            key: "agent_type",
            label: "全部类型",
            type: "select",
            options: [
              { value: "normalize", label: "提示词规范化" },
              { value: "enhance", label: "提示词增强" },
              { value: "safety_check", label: "安全检测" },
              { value: "translate", label: "翻译" },
              { value: "summarize", label: "摘要" },
            ],
          },
          {
            key: "status",
            label: "全部状态",
            type: "select",
            options: [
              { value: "success", label: "成功" },
              { value: "failed", label: "失败" },
              { value: "timeout", label: "超时" },
            ],
          },
        ]}
        filterValues={{ agent_type: agentTypeFilter, status: statusFilter }}
        onFilterChange={(key, val) => {
          if (key === "agent_type") setAgentTypeFilter(val);
          if (key === "status") setStatusFilter(val);
          setPage(1);
        }}
        onReset={() => { setGenIdFilter(""); setAgentTypeFilter(""); setStatusFilter(""); setSortField("created_at"); setSortDir("desc"); setPage(1); }}
      />

      {/* Table */}
      <div className="bg-white rounded-xl border border-surface-border overflow-hidden">
        <div className="overflow-x-auto -mx-0">
          <table className="w-full text-sm min-w-[900px]">
            <thead>
              <tr className="border-b border-surface-border bg-surface/50">
                <th className="text-left px-3 md:px-5 py-3 md:py-3.5 text-text-secondary font-medium text-xs">任务ID</th>
                <th className="text-left px-3 md:px-5 py-3 md:py-3.5 text-text-secondary font-medium text-xs">用户</th>
                <th className="text-left px-3 md:px-5 py-3 md:py-3.5 text-text-secondary font-medium text-xs">Agent类型</th>
                <th className="text-left px-3 md:px-5 py-3 md:py-3.5 text-text-secondary font-medium text-xs">状态</th>
                <th className="text-left px-3 md:px-5 py-3 md:py-3.5 text-text-secondary font-medium text-xs">模型</th>
                <TableSortHeader label="延迟" field="latency_ms" currentField={sortField} currentDir={sortDir} onSort={handleSort} />
                <TableSortHeader label="时间" field="created_at" currentField={sortField} currentDir={sortDir} onSort={handleSort} className="hidden sm:table-cell" />
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={7} className="px-5 py-12 text-center text-text-muted">
                    <i className="ri-loader-4-line animate-spin text-2xl mb-2 block" />
                    加载中...
                  </td>
                </tr>
              ) : traces.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-5 py-12 text-center text-text-muted">
                    <i className="ri-robot-2-line text-3xl mb-2 block" />
                    暂无追踪记录
                  </td>
                </tr>
              ) : (
                traces.map((t) => {
                  const statusCfg = statusLabels[t.status] || statusLabels.success;
                  return (
                    <tr key={t.id} className="border-b border-surface-border hover:bg-surface/30 transition-colors">
                      <td className="px-3 md:px-5 py-3 md:py-4">
                        <p className="text-text-primary text-xs font-mono">{t.task_id?.slice(0, 20) || "--"}</p>
                        <p className="text-text-muted text-[10px] font-mono mt-0.5">{t.gen_id ? `gen: ${t.gen_id.slice(-8)}` : "--"}</p>
                      </td>
                      <td className="px-3 md:px-5 py-3 md:py-4">
                        <UserIdentityCell nickname={t.nickname} userId={t.user_id} avatar={t.avatar} />
                      </td>
                      <td className="px-3 md:px-5 py-3 md:py-4">
                        <span className="inline-flex px-2 py-1 rounded-full text-[11px] font-medium bg-surface text-text-secondary">
                          {agentTypeLabels[t.agent_type] || t.agent_type}
                        </span>
                      </td>
                      <td className="px-3 md:px-5 py-3 md:py-4">
                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-[11px] font-medium ${statusCfg.cls}`}>
                          <i className={t.status === "success" ? "ri-checkbox-circle-line" : t.status === "failed" ? "ri-close-circle-line" : "ri-time-line"} />
                          {statusCfg.label}
                        </span>
                      </td>
                      <td className="px-3 md:px-5 py-3 md:py-4 text-text-muted text-xs">
                        {t.model_used || "--"}
                      </td>
                      <td className="px-3 md:px-5 py-3 md:py-4 font-mono text-text-primary text-xs">
                        {t.latency_ms ? `${t.latency_ms}ms` : "--"}
                      </td>
                      <td className="px-3 md:px-5 py-3 md:py-4 text-text-muted text-xs hidden sm:table-cell">
                        {formatDateTime(t.created_at)}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <Pagination
            page={page}
            totalPages={totalPages}
            total={total}
            pageSize={pageSize}
            onPageChange={setPage}
            onPageSizeChange={(size) => { setPageSize(size); setPage(1); }}
          />
        )}
      </div>
    </div>
  );
}
